import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminRoofCategory, UpsertAdminRoofCategoryBody } from "../../../../../http/patrol/types/roofCategories";
import { useRoofCategoriesApi } from "../../../../../http/patrol/useRoofCategories";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Inline from "../../../../ui/Inline";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import Switch from "../../../../ui/Switch";
import TranslatableField from "../../../../ui/TranslatableField";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface UpdateRoofCategoryForm {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  isFlat: boolean;
  minPitch: number | null;
  maxPitch: number | null;
  defaultPitch: number | null;
}

interface RoofCategoryDetailsProps {
  roofCategory?: AdminRoofCategory;
  disabled?: boolean;
}

const RoofCategoryDetails: FC<RoofCategoryDetailsProps> = ({ roofCategory, disabled }: RoofCategoryDetailsProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const roofCategoriesApi = useRoofCategoriesApi();

  const defaultValues: UpdateRoofCategoryForm = {
    name: roofCategory?.name ?? null,
    nameIt: roofCategory?.nameIt ?? null,
    nameDe: roofCategory?.nameDe ?? null,
    nameEs: roofCategory?.nameEs ?? null,
    nameFr: roofCategory?.nameFr ?? null,
    order: roofCategory?.order ?? 50,
    visibility: roofCategory?.visibility ?? "hidden",
    isFlat: roofCategory?.isFlat ?? false,
    minPitch: roofCategory?.minPitch ?? null,
    maxPitch: roofCategory?.maxPitch ?? null,
    defaultPitch: roofCategory?.defaultPitch ?? null,
  };

  const onSubmit = () => async (formData: UpdateRoofCategoryForm) => {
    setIsSubmitting(true);

    const body: UpsertAdminRoofCategoryBody = {
      name: formData.name?.trim()!,
      nameIt: formData.nameIt?.trim() ?? null,
      nameDe: formData.nameDe?.trim() ?? null,
      nameEs: formData.nameEs?.trim() ?? null,
      nameFr: formData.nameFr?.trim() ?? null,
      order: formData.order!,
      visibility: formData.visibility,
      isFlat: formData.isFlat,
      minPitch: formData.minPitch,
      maxPitch: formData.maxPitch,
      defaultPitch: formData.defaultPitch,
    };

    try {
      await roofCategoriesApi.updateAdminRoofCategory(roofCategory?.id!, body);
      dispatch(showSuccessNotification(i18n.translation));
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form<UpdateRoofCategoryForm>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || disabled}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, reset, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [roofCategory]);

        useEffect(() => {
          register("nameIt");
          register("nameDe");
          register("nameEs");
          register("nameFr");
        }, [register]);

        const values = watch();

        const flat = !!values.isFlat;

        return (
          <FormFieldsContainer>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isOptional, isInvalid }) => {
                return (
                  <TranslatableField
                    field="name"
                    formValues={values}
                    setFormValue={setValue}
                    dialogTitle={i18n.translation.common.name}
                  >
                    <Input
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={disabled}
                      {...register("name", {
                        required: !isOptional,
                        minLength: 1,
                      })}
                    />
                  </TranslatableField>
                );
              }}
            </FormField>
            <FormField
              label={`${i18n.translation.common.pitch} (${i18n.translation.common.min}/${i18n.translation.common.max}/${i18n.translation.common.default})`}
            >
              <Inline>
                <Controller
                  name="minPitch"
                  control={control}
                  rules={{ max: 100 }}
                  render={({ field, fieldState }) => (
                    <FormField error={fieldState.error} isOptional>
                      {({ labelId, isInvalid }) => (
                        <Numeric
                          {...field}
                          id={labelId}
                          disabled={disabled}
                          isInvalid={isInvalid}
                          decimalScale={0}
                          allowNegative={false}
                          suffix=" °"
                          onBlur={(value) => setValue("minPitch", value)}
                        />
                      )}
                    </FormField>
                  )}
                />
                <Controller
                  name="maxPitch"
                  control={control}
                  rules={{ max: 100 }}
                  render={({ field, fieldState }) => (
                    <FormField error={fieldState.error} isOptional>
                      {({ labelId, isInvalid }) => (
                        <Numeric
                          {...field}
                          id={labelId}
                          disabled={disabled}
                          isInvalid={isInvalid}
                          decimalScale={0}
                          allowNegative={false}
                          suffix=" °"
                          onBlur={(value) => setValue("maxPitch", value)}
                        />
                      )}
                    </FormField>
                  )}
                />
                <Controller
                  name="defaultPitch"
                  control={control}
                  rules={{ max: 100 }}
                  render={({ field, fieldState }) => (
                    <FormField error={fieldState.error} isOptional>
                      {({ labelId, isInvalid }) => (
                        <Numeric
                          {...field}
                          id={labelId}
                          disabled={disabled}
                          isInvalid={isInvalid}
                          decimalScale={0}
                          allowNegative={false}
                          suffix=" °"
                          onBlur={(value) => setValue("defaultPitch", value)}
                        />
                      )}
                    </FormField>
                  )}
                />
              </Inline>
            </FormField>
            <FormField label={i18n.translation.common.isFlat} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={flat}
                  disabled={disabled}
                  onChange={(checked) => setValue("isFlat", checked)}
                />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={disabled}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default RoofCategoryDetails;
